import { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api-service';
import { fetchTv3PlayAvailability, fetchTmdbLtLocalizedName } from '../services/tv3-play-service';
import MediaActionButtons from './media-action-buttons';
import AuthModal from './auth-modal';
import MetaTags from './meta-tags';
import { CONSOLE_MESSAGES, TMDB_IMAGE_URLS } from '../constants';
import { BUTTON_CLASSES } from '../styles';
import { useLanguage } from '../contexts/language-context';
import { translateTVStatus, translateCountry, translateLanguage } from '../utils/translation-helpers';
import { TeliaPlayLogo, Go3Logo, TV3PlayLogo, getProviderDisplayName } from './provider-logos';
import useGoBack from '../hooks/use-go-back';

const MovieComments = lazy(() => import('./movie-comments'));

// Loading Header Component for TV Shows
const LoadingTVHeader = () => {
  const { t } = useLanguage();
  const goBack = useGoBack('/tv');
  
  return (
  <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-dark-900 to-gray-800">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-20">
      <div className="w-full h-full" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(139, 69, 19, 0.3) 0%, transparent 50%), 
                         radial-gradient(circle at 75% 75%, rgba(30, 58, 138, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 50% 50%, rgba(75, 85, 99, 0.2) 0%, transparent 50%)`,
        backgroundSize: '100% 100%, 80% 80%, 60% 60%'
      }} />
    </div>
    
    {/* Back Button */}
    <div className="relative z-10 pt-8 pb-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={goBack}
          className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
          aria-label={t('goBack')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('goBack')}</span>
        </button>
      </div>
    </div>

    {/* Loading Content */}
    <div className="relative z-10 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
          {/* Loading Poster */}
          <div className="flex-shrink-0 mx-auto lg:mx-0">
            <div className="w-80 h-120 rounded-xl shadow-2xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 animate-pulse border border-gray-600"></div>
          </div>

          {/* Loading Info */}
          <div className="flex-1 text-center lg:text-left">
            <div className="h-16 lg:h-20 bg-gray-700 rounded-lg animate-pulse mb-4"></div>
            <div className="h-6 bg-gray-700 rounded animate-pulse mb-6 w-3/4"></div>
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-6">
              <div className="h-10 w-24 bg-gray-700 rounded animate-pulse"></div>
              <div className="h-10 w-32 bg-gray-700 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

// Error Header Component for TV Shows
const ErrorTVHeader = ({ error }) => {
  const { t } = useLanguage();
  const goBack = useGoBack('/tv');
  
  return (
  <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-dark-900 to-gray-800">
    {/* Back Button */}
    <div className="relative z-10 pt-8 pb-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={goBack}
          className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
          aria-label={t('goBack')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('goBack')}</span>
        </button>
      </div>
    </div>

    {/* Error Content */}
    <div className="relative z-10 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">TV serialas nerastas</h1>
          <p className="text-gray-400 text-lg mb-8">
            {error?.includes('Item not found') 
              ? 'Atsiprašome, šio TV serialo informacija šiuo metu neprieinama. Gali būti, kad serialas buvo pašalintas iš duomenų bazės arba įvyko techninis nesklandumas.'
              : error || 'Įvyko nežinoma klaida kraunant TV serialo informaciją.'
            }
          </p>
          <div className="space-x-4">
            <button
              onClick={goBack}
              className={BUTTON_CLASSES.PRIMARY}
            >
              {t('goBack')}
            </button>
            <button
              onClick={() => window.location.href = '/tv'}
              className="bg-dark-700 hover:bg-dark-600 text-white px-6 py-3 rounded-lg transition-colors"
            >
              {t('tvShows')}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

function TVDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack('/tv');
  const { t } = useLanguage();
  const [tvShow, setTvShow] = useState(null);
  const [cast, setCast] = useState([]);
  const [videos, setVideos] = useState([]);
  const [similarShows, setSimilarShows] = useState([]);
  const [streamingProviders, setStreamingProviders] = useState(null);
  const [externalProviders, setExternalProviders] = useState(null);
  const [hasExternalAvailability, setHasExternalAvailability] = useState(false);
  const [tv3PlayLookupDone, setTv3PlayLookupDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  const imageBaseUrl = TMDB_IMAGE_URLS.W500;
  const backdropBaseUrl = TMDB_IMAGE_URLS.W1280;

  // Professional poster fallback component
  const PosterFallback = () => (
    <div className="w-80 h-120 rounded-xl shadow-2xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex flex-col items-center justify-center border border-gray-600">
      <div className="p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84.95 1.54 2.2 2.86 3.16 4.4.47.75.75 1.6.43 2.76H10c-1.1 0-2 .9-2 2s.9 2 2 2h4c1.1 0 2-.9 2-2s-.9-2-2-2h-.79c-.32-1.16-.04-2.01.43-2.76.96-1.54 2.21-2.86 3.16-4.4C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7zm0 2c2.76 0 5 2.24 5 5 0 1.64-.68 3.06-1.39 4.24-.75 1.26-1.49 2.69-1.49 4.76 0 .34-.03.68-.12 1H10c-.09-.32-.12-.66-.12-1 0-2.07-.74-3.5-1.49-4.76C7.68 12.06 7 10.64 7 9c0-2.76 2.24-5 5-5z"/>
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-200 mb-2">{tvShow?.name || 'TV Serialas'}</h3>
        <p className="text-sm text-gray-400">Nuotrauka nepasiekiama</p>
      </div>
    </div>
  );

  const handleRequireAuth = () => {
    setAuthModalMode('register');
    setShowAuthModal(true);
  };

  // Scroll to top when component mounts or TV ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    const fetchTVDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        setTv3PlayLookupDone(false);

        // Create a timeout promise that rejects after 4 seconds
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('Request timeout'));
          }, 4000);
        });

        // Race between the API call and the timeout
        const dataPromise = apiService.getTVDetailsComplete(id);
        
        let data;
        try {
          data = await Promise.race([dataPromise, timeoutPromise]);
          // Clear timeout if request succeeded
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        } catch (timeoutError) {
          // If timeout occurs, wait a bit more for the actual request to potentially complete
          // This gives us partial data if the main API succeeds but external providers are slow
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          console.warn('Primary request timeout, waiting for response...', timeoutError.message);
          
          // Wait up to 1 more second for the actual request
          try {
            data = await Promise.race([
              dataPromise,
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Final timeout')), 1000)
              )
            ]);
          } catch (finalError) {
            // If still no response, throw the original error
            throw timeoutError;
          }
        }

        if (!mounted) return;

        // Set TV show details
        if (data?.details) {
          setTvShow(data.details);
        }

        // Set cast (limited to 12)
        if (data?.credits?.cast) {
          setCast(data.credits.cast.slice(0, 12));
        }

        // Set videos (only trailers)
        if (data?.videos?.results) {
          const trailers = data.videos.results.filter(video => video.type === 'Trailer');
          setVideos(trailers);
          // Set first trailer as selected
          if (trailers.length > 0) {
            setSelectedTrailer(trailers[0]);
          }
        }

        // Set similar TV shows (limited to 8)
        if (data?.similar?.results) {
          setSimilarShows(data.similar.results.slice(0, 8));
        }

        // Set streaming providers for Lithuania (TMDB)
        let lithuanianProviders = {};
        if (data?.providers?.results) {
          lithuanianProviders = data.providers.results.LT || {};
        }

        // Check external providers (e.g., Telia Play, Go3, Forum Cinemas, Cinamon)
        // Handle providers gracefully - if some fail, just skip them
        let externalAvailable = false;
        let externalProvidersData = {};
        if (data?.providers?.external) {
          // Filter out providers that have errors or failed to load
          Object.entries(data.providers.external).forEach(([key, ext]) => {
            if (ext?.exists && !ext.error) {
              externalAvailable = true;
            }
          });
          externalProvidersData = data.providers.external;
        }

        // Store provider data
        setStreamingProviders(lithuanianProviders);
        setExternalProviders(externalProvidersData);
        setHasExternalAvailability(externalAvailable);

        if (data?.details && mounted) {
          try {
            // TV3 LT catalogue is keyed on Lithuanian titles; English queries return no PROGRAMME rows.
            // Prefer TMDB lt title (direct API if REACT_APP_TMDB_API_KEY), else app API, else overview hints in fetchTv3PlayAvailability.
            let tv3DisplayTitle = data.details.name;
            try {
              const ltFromTmdb = await fetchTmdbLtLocalizedName(id, 'tv');
              if (ltFromTmdb) tv3DisplayTitle = ltFromTmdb;
              else {
                const ltDetails = await apiService.getTVDetails(id, 'lt');
                const ltName = ltDetails?.name?.trim();
                if (ltName) tv3DisplayTitle = ltName;
              }
            } catch {
              // optional
            }
            const tv3 = await fetchTv3PlayAvailability({
              title: tv3DisplayTitle,
              originalTitle: data.details.original_name || data.details.name,
              overview: data.details.overview,
            });
            if (mounted && tv3.exists) {
              setExternalProviders((prev) => ({
                ...prev,
                tv3Play: {
                  exists: true,
                  error: false,
                  programDetails: tv3.programDetails,
                },
              }));
              setHasExternalAvailability(true);
            }
          } catch {
            // TV3 Play is optional
          }
        }

      } catch (err) {
        if (mounted) {
          const errorMessage = err?.message?.toLowerCase() || '';
          const isTimeout = errorMessage.includes('timeout');
          
          if (isTimeout) {
            console.warn('Request timed out - TV show data may be incomplete', err);
            // For timeout, just show error message, data will be whatever loaded
            setError('Some data may be unavailable due to slow response');
          } else {
            setError(err.message);
            console.error(CONSOLE_MESSAGES.ERROR_FETCHING_TV, err);
          }
        }
      } finally {
        // Always clear timeout and set loading to false
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (mounted) {
          setLoading(false);
          setTv3PlayLookupDone(true);
        }
      }
    };

    fetchTVDetails();

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [id]);

  const formatRuntime = (minutes) => {
    if (!minutes || minutes.length === 0) return 'Nenustatyta';
    const avgRuntime = Math.round(minutes.reduce((a, b) => a + b, 0) / minutes.length);
    const hours = Math.floor(avgRuntime / 60);
    const mins = avgRuntime % 60;
    return hours > 0 ? `${hours}val ${mins}min` : `${mins}min`;
  };


  const getGenreNames = (genres) => {
    const genreMap = {
      10759: 'Veiksmo ir nuotykių', 16: 'Animacijos', 35: 'Komedijos',
      80: 'Kriminalinis', 99: 'Dokumentinis', 18: 'Drama', 10751: 'Šeimos',
      10762: 'Vaikų', 9648: 'Misterija', 10763: 'Naujienų', 10764: 'Realybės',
      10765: 'Mokslinės fantastikos ir fantastikos', 10766: 'Muilinės operos',
      10767: 'Pokalbių šou', 10768: 'Karo ir politikos', 37: 'Vesternų'
    };
    return genres?.map(genre => genreMap[genre.id] || genre.name) || [];
  };

  // Get IMDB and Rotten Tomatoes ratings
  const getRatingData = () => {
    const data = {
      imdb: null,
      rt: null,
      tmdb: null,
      hasRealData: false
    };

    // Check for real IMDB and RT ratings
    if (tvShow?.enhanced_ratings?.real_ratings) {
      data.hasRealData = true;
      if (tvShow.enhanced_ratings.real_ratings.imdb?.rating) {
        data.imdb = tvShow.enhanced_ratings.real_ratings.imdb.rating.toFixed(1);
      }
      if (tvShow.enhanced_ratings.real_ratings.rottenTomatoes?.rating) {
        data.rt = tvShow.enhanced_ratings.real_ratings.rottenTomatoes.rating;
      }
    }
    
    // ONLY show TMDB when neither IMDB nor RT are available
    const hasImdb = data.imdb !== null && data.imdb !== undefined;
    const hasRt = data.rt !== null && data.rt !== undefined;
    
    if (!hasImdb && !hasRt && tvShow?.vote_average && tvShow.vote_average > 0) {
      data.tmdb = tvShow.vote_average.toFixed(1);
    }

    return data;
  };

  const renderStreamingProviders = () => {
    const providerTypes = [
      { key: 'flatrate', labelKey: 'subscription', icon: '📺', color: 'from-blue-500 to-blue-600' },
      { key: 'buy', labelKey: 'buy', icon: '💳', color: 'from-green-500 to-green-600' },
      { key: 'rent', labelKey: 'rent', icon: '🎬', color: 'from-purple-500 to-purple-600' },
      { key: 'free', labelKey: 'free', icon: '🆓', color: 'from-orange-500 to-orange-600' }
    ];

    const hasAnyTMDBProviders = streamingProviders && typeof streamingProviders === 'object' && 
      providerTypes.some(type => streamingProviders[type.key]?.length > 0);

    // Show section if we have TMDB providers OR external providers
    if (!hasAnyTMDBProviders && !hasExternalAvailability) return null;

    return (
      <section>
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15M6 6v12a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2H8a2 2 0 00-2 2z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white">{t('whereToWatch')}</h2>
        </div>
        
        <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl p-8 border border-dark-700/50 shadow-2xl">
          <div className="space-y-8">
            {/* TMDB Providers + External Providers */}
            {providerTypes.map((type) => {
              const tmdbProviders = streamingProviders?.[type.key] || [];
              
              // Add external providers to appropriate categories based on pricing/subscription
              let allProviders = [...tmdbProviders];
              if (hasExternalAvailability && externalProviders) {
                const availableExternalProviders = Object.entries(externalProviders)
                  .filter(([providerId, provider]) => 
                    provider?.exists && 
                    !provider?.error && 
                    providerId !== 'forumCinemas' && 
                    providerId !== 'cinamon'
                  )
                  .map(([providerId, provider]) => {
                    let price = 0;
                    let isFree = false;
                    let isSubscription = false;
                    let isRental = false;
                    let programDetails = provider.programDetails;
                    
                    // Handle Go3 - can be rental, subscription, or free depending on content
                    if (providerId === 'go3' && programDetails) {
                      price = programDetails.price || 0;
                      isFree = programDetails.isFree || false;
                      isSubscription = programDetails.isSubscription || false;
                      isRental = programDetails.isRental || false;
                      
                      // If it's VOD rental without a price, still show as rental
                      if (programDetails.isVod && !isFree && !isSubscription) {
                        isRental = true;
                      }
                    } else if (providerId === 'tv3Play' && programDetails) {
                      isFree = true;
                      isSubscription = false;
                      isRental = false;
                    }
                    // Handle Telia Play format: raw.programs[0].asset.p
                    else if (provider.raw?.programs?.length > 0) {
                      const program = provider.raw.programs[0];
                      if (program.asset?.p !== undefined) {
                        price = program.asset.p;
                        isFree = price === 0;
                        isRental = price > 0; // Telia Play shows rental prices
                        programDetails = {
                          price: price,
                          currency: 'EUR',
                          isFree: isFree,
                          isRental: isRental,
                          title: program.asset.label || program.asset.fullTitle,
                          originalTitle: program.asset.originalTitle
                        };
                      }
                    } 
                    // Fallback to programDetails if it exists
                    else if (programDetails?.price !== undefined) {
                      price = programDetails.price;
                      isFree = programDetails.isFree || price === 0;
                      isSubscription = programDetails.isSubscription || false;
                      isRental = programDetails.isRental || (price > 0 && !isSubscription);
                    }
                    
                    return {
                      provider_id: providerId,
                      provider_name: getProviderDisplayName(providerId),
                      logo_path: null,
                      isExternal: true,
                      externalId: providerId,
                      price: price,
                      currency: programDetails?.currency || 'EUR',
                      isFree: isFree,
                      isSubscription: isSubscription,
                      isRental: isRental,
                      programDetails: programDetails
                    };
                  });
                
                // Add to appropriate category based on type
                if (type.key === 'flatrate') {
                  // Add subscription-based providers to subscription category
                  const subscriptionProviders = availableExternalProviders.filter(p => p.isSubscription && !p.isRental);
                  allProviders = [...allProviders, ...subscriptionProviders];
                } else if (type.key === 'free') {
                  // Add free external providers to free category
                  const freeProviders = availableExternalProviders.filter(p => p.isFree && !p.isSubscription && !p.isRental);
                  allProviders = [...allProviders, ...freeProviders];
                } else if (type.key === 'rent') {
                  // Add rental providers to rent category (VOD rentals like Go3)
                  const rentalProviders = availableExternalProviders.filter(p => p.isRental);
                  allProviders = [...allProviders, ...rentalProviders];
                } else if (type.key === 'buy') {
                  // Add paid external providers (purchases) to buy category
                  const purchaseProviders = availableExternalProviders.filter(p => !p.isFree && !p.isSubscription && !p.isRental);
                  allProviders = [...allProviders, ...purchaseProviders];
                }
                
                // Deduplicate providers by name (case-insensitive)
                // TMDB may already include Go3, Telia Play, etc., so remove external duplicates
                const seenNames = new Set();
                allProviders = allProviders.filter(provider => {
                  const normalizedName = provider.provider_name?.toLowerCase() || '';
                  if (seenNames.has(normalizedName)) {
                    return false; // Skip duplicate
                  }
                  seenNames.add(normalizedName);
                  return true;
                });
              }

              if (allProviders.length === 0) return null;

              return (
                <div key={type.key} className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${type.color} rounded-lg flex items-center justify-center`}>
                      <span className="text-white text-lg">{type.icon}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{t(type.labelKey)}</h3>
                    <div className="flex-1 h-px bg-gradient-to-r from-dark-600 to-transparent"></div>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {allProviders.map((provider) => {
                      const isTv3Link =
                        provider.isExternal &&
                        provider.externalId === 'tv3Play' &&
                        provider.programDetails?.url;
                      const cardClass =
                        'group relative bg-dark-700/50 backdrop-blur-sm rounded-xl p-4 hover:bg-dark-600/60 hover:scale-105 transition-all duration-300 cursor-pointer border border-dark-600/30 hover:border-primary-500/30';
                      const cardInner = (
                        <>
                          <div className="flex flex-col items-center space-y-3">
                            <div
                              className={`relative w-14 h-14 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow flex items-center justify-center ${
                                provider.isExternal && provider.externalId === 'teliaPlay'
                                  ? 'bg-black p-2'
                                  : provider.isExternal && provider.externalId === 'go3'
                                    ? 'bg-transparent'
                                    : provider.isExternal && provider.externalId === 'tv3Play'
                                      ? 'bg-[#1a1a1a] p-1'
                                      : 'bg-white'
                              }`}
                            >
                              {provider.isExternal && provider.externalId === 'teliaPlay' ? (
                                <TeliaPlayLogo />
                              ) : provider.isExternal && provider.externalId === 'go3' ? (
                                <Go3Logo />
                              ) : provider.isExternal && provider.externalId === 'tv3Play' ? (
                                <TV3PlayLogo />
                              ) : (
                                <img
                                  src={`${TMDB_IMAGE_URLS.ORIGINAL}${provider.logo_path}`}
                                  alt={provider.provider_name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </div>
                            <span className="text-white text-sm font-medium text-center leading-tight group-hover:text-primary-300 transition-colors">
                              {provider.provider_name}
                            </span>
                            {provider.isExternal && provider.isSubscription && !provider.isRental && (
                              <span className="text-blue-400 text-xs font-bold text-center">
                                {t('subscription')}
                              </span>
                            )}
                            {provider.isExternal && provider.isRental && (
                              <span className="text-purple-400 text-xs font-bold text-center">
                                {provider.price > 0
                                  ? `${provider.price.toFixed(2)} ${provider.currency}`
                                  : t('rent')}
                              </span>
                            )}
                            {provider.isExternal &&
                              provider.price !== undefined &&
                              provider.price > 0 &&
                              !provider.isFree &&
                              !provider.isSubscription &&
                              !provider.isRental && (
                                <span className="text-primary-400 text-xs font-bold text-center">
                                  {provider.price.toFixed(2)} {provider.currency}
                                </span>
                              )}
                            {provider.isExternal && provider.isFree && !provider.isSubscription && !provider.isRental && (
                              <span className="text-green-400 text-xs font-bold text-center">
                                {t('free')}
                              </span>
                            )}
                          </div>
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary-500/5 to-primary-600/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </>
                      );
                      return isTv3Link ? (
                        <a
                          key={provider.externalId}
                          href={provider.programDetails.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cardClass}
                          title={provider.provider_name}
                        >
                          {cardInner}
                        </a>
                      ) : (
                        <div
                          key={provider.isExternal ? provider.externalId : provider.provider_id}
                          className={cardClass}
                          title={provider.provider_name}
                        >
                          {cardInner}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return <LoadingTVHeader />;
  }

  if (error) {
    return <ErrorTVHeader error={error} />;
  }

  if (!tvShow) {
    return <ErrorTVHeader error="TV serialas nerastas" />;
  }

  return (
    <>
      <MetaTags
        title={`${tvShow.name} (${tvShow.first_air_date?.split('-')[0]}) - FilmųDB`}
        description={tvShow.overview || `Žiūrėkite ${tvShow.name} - ${getGenreNames(tvShow.genres).join(', ')}. TV serialo informacija, aktoriai, anonsas ir kur žiūrėti Lietuvoje.`}
        image={tvShow.backdrop_path ? `${TMDB_IMAGE_URLS.W1280}${tvShow.backdrop_path}` : `${TMDB_IMAGE_URLS.W500}${tvShow.poster_path}`}
        url={`https://filmudb.lt/tv/${tvShow.id}`}
        type="video.tv_show"
        keywords={`${tvShow.name}, TV serialas, ${getGenreNames(tvShow.genres).join(', ')}, ${tvShow.first_air_date?.split('-')[0]}, televizija, anonsas`}
      />
      <div className="min-h-screen bg-dark-900">
        {/* Backdrop or Professional Fallback Background */}
        <div className="relative">
          <div className="absolute inset-0 z-0">
            {tvShow.backdrop_path ? (
              <>
                <img
                  src={`${backdropBaseUrl}${tvShow.backdrop_path}`}
                  alt={tvShow.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/70 to-dark-900/30"></div>
              </>
            ) : (
              /* Professional gradient background when no backdrop exists */
              <div className="w-full h-full bg-gradient-to-br from-gray-900 via-dark-900 to-gray-800">
                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-20">
                  <div className="w-full h-full" style={{
                    backgroundImage: `radial-gradient(circle at 25% 25%, rgba(139, 69, 19, 0.3) 0%, transparent 50%), 
                                     radial-gradient(circle at 75% 75%, rgba(30, 58, 138, 0.3) 0%, transparent 50%),
                                     radial-gradient(circle at 50% 50%, rgba(75, 85, 99, 0.2) 0%, transparent 50%)`,
                    backgroundSize: '100% 100%, 80% 80%, 60% 60%'
                  }} />
                </div>
                {/* Additional gradient overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/60 to-dark-900/20" />
              </div>
            )}
          </div>

          {/* Back Button */}
          <div className="relative z-10 pt-8 pb-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <button
                onClick={goBack}
                className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                <span>{t('goBack')}</span>
              </button>
            </div>
          </div>

          {/* TV Show Header */}
          <div className="relative z-10 pb-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
                {/* TV Show Poster */}
                <div className="flex-shrink-0 mx-auto lg:mx-0">
                  <div className="relative">
                    {tvShow.poster_path ? (
                      <img
                        src={`${imageBaseUrl}${tvShow.poster_path}`}
                        alt={tvShow.name}
                        className="w-80 h-120 rounded-xl shadow-2xl object-cover"
                        loading="eager"
                      />
                    ) : (
                      <PosterFallback />
                    )}
                  </div>
                </div>

                {/* TV Show Info */}
                <div className="flex-1 text-white text-center lg:text-left">
                  <h1 className="text-4xl lg:text-6xl font-bold mb-4">{tvShow.name}</h1>
                  
                  {tvShow.tagline && (
                    <p className="text-xl text-gray-300 italic mb-6">"{tvShow.tagline}"</p>
                  )}

                  {/* TV Show Ratings */}
                  {(() => {
                    const ratingData = getRatingData();
                    if (!ratingData.imdb && !ratingData.rt && !ratingData.tmdb) return null;
                    
                    return (
                      <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 mb-6">
                        {/* IMDB Rating */}
                        {ratingData.imdb && (
                          <div className="flex items-center space-x-2 bg-yellow-600/20 px-4 py-2 rounded-lg">
                            <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-yellow-400 font-bold text-lg">{ratingData.imdb}</span>
                            <span className="text-yellow-300 font-medium">IMDB</span>
                          </div>
                        )}
                        
                        {/* Rotten Tomatoes Rating */}
                        {ratingData.rt && (
                          <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                            ratingData.rt >= 60 ? 'bg-red-600/20' : 'bg-green-600/20'
                          }`}>
                            <svg className={`w-5 h-5 ${ratingData.rt >= 60 ? 'text-red-400' : 'text-green-400'}`} 
                                 fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                            </svg>
                            <span className={`font-bold text-lg ${
                              ratingData.rt >= 60 ? 'text-red-400' : 'text-green-400'
                            }`}>
                              {ratingData.rt}%
                            </span>
                            <span className="text-gray-300 font-medium">Rotten Tomatoes</span>
                          </div>
                        )}

                        {/* TMDB Rating - Only show as fallback when no real ratings */}
                        {ratingData.tmdb && (
                          <div className="flex items-center space-x-2 bg-blue-600/20 px-4 py-2 rounded-lg">
                            <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-blue-400 font-bold text-lg">{ratingData.tmdb}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Availability Warning — hide until TV3 lookup finishes */}
                  {tv3PlayLookupDone && streamingProviders && (
                    !streamingProviders.flatrate?.length && 
                    !streamingProviders.buy?.length && 
                    !streamingProviders.rent?.length && 
                    !streamingProviders.free?.length &&
                    !hasExternalAvailability
                  ) && (
                    <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-center lg:justify-start space-x-3">
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div className="text-center lg:text-left">
                          <h3 className="text-amber-200 font-medium">Neprieinamas Lietuvoje</h3>
                          <p className="text-amber-300/80 text-sm mt-1">
                            Šis TV serialas šiuo metu neprieinamas žiūrėjimui Lietuvoje per oficialias platformas
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Meta Info */}
                  <div className="flex flex-wrap justify-center lg:justify-start items-center gap-6 mb-6">
                    <span className="text-gray-300">{tvShow.first_air_date?.split('-')[0]}</span>
                    {tvShow.episode_run_time && tvShow.episode_run_time.length > 0 && (
                      <span className="text-gray-300">{formatRuntime(tvShow.episode_run_time)} per epizodą</span>
                    )}
                    
                    {tvShow.adult && (
                      <span className="bg-red-600 text-white px-2 py-1 rounded text-sm">18+</span>
                    )}
                  </div>

                  {/* TV Show specific info */}
                  <div className="flex flex-wrap justify-center lg:justify-start items-center gap-6 mb-6">
                    {tvShow.number_of_seasons && (
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <span className="text-gray-300">
                          {tvShow.number_of_seasons} {tvShow.number_of_seasons === 1 ? t('season') : t('seasons')}
                        </span>
                      </div>
                    )}
                    
                    {tvShow.number_of_episodes && (
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 3v10a2 2 0 002 2h6a2 2 0 002-2V7H7z" />
                        </svg>
                        <span className="text-gray-300">{tvShow.number_of_episodes} {t('episodes')}</span>
                      </div>
                    )}

                    {tvShow.status && (
                      <span className={`px-2 py-1 rounded text-sm ${
                        tvShow.status === 'Ended' ? 'bg-red-600 text-white' :
                        tvShow.status === 'Returning Series' ? 'bg-green-600 text-white' :
                        'bg-blue-600 text-white'
                      }`}>
                        {translateTVStatus(tvShow.status)}
                      </span>
                    )}
                  </div>

                  {/* Genres */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
                    {getGenreNames(tvShow.genres).map((genre, index) => (
                      <span
                        key={`${tvShow.id}-genre-${index}`}
                        className="bg-primary-600/20 text-primary-300 px-3 py-1 rounded-full text-sm border border-primary-500/30"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>

                  {/* Overview */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold mb-3">{t('description')}</h2>
                    <p className="text-gray-300 leading-relaxed text-lg">
                      {tvShow.overview || t('noDescription')}
                    </p>
                  </div>

                  {/* Additional Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-center lg:text-left">
                    {tvShow.networks?.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t('streamingOn')}</h3>
                        <p className="text-gray-300">
                          {tvShow.networks.map(network => network.name).join(', ')}
                        </p>
                      </div>
                    )}
                    
                    {tvShow.production_companies?.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t('studio')}</h3>
                        <p className="text-gray-300">
                          {tvShow.production_companies.map(company => company.name).join(', ')}
                        </p>
                      </div>
                    )}
                    
                    {tvShow.origin_country?.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t('country')}</h3>
                        <p className="text-gray-300">
                          {tvShow.origin_country.map(translateCountry).join(', ')}
                        </p>
                      </div>
                    )}

                    {tvShow.original_language && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t('mediaLanguage')}</h3>
                        <p className="text-gray-300">{translateLanguage(tvShow.original_language)}</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center lg:justify-start">
                    <MediaActionButtons 
                      item={tvShow} 
                      mediaType="tv"
                      onRequireAuth={handleRequireAuth}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="bg-dark-900 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            
            {/* Streaming Providers */}
            {renderStreamingProviders()}
            
            {/* Trailers */}
            {videos.length > 0 && (
              <section>
                <h2 className="text-3xl font-bold text-white mb-8">{t('trailers')}</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    {selectedTrailer && (
                      <div className="aspect-video bg-dark-800 rounded-xl overflow-hidden">
                        <iframe
                          src={`https://www.youtube.com/embed/${selectedTrailer.key}`}
                          title={selectedTrailer.name}
                          className="w-full h-full"
                          allowFullScreen
                        ></iframe>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    {videos.map((video) => (
                      <button
                        key={video.id}
                        onClick={() => setSelectedTrailer(video)}
                        className={`w-full text-left p-4 rounded-lg transition-colors ${
                          selectedTrailer?.id === video.id
                            ? 'bg-primary-600/20 border border-primary-500'
                            : 'bg-dark-800 hover:bg-dark-700'
                        }`}
                      >
                        <h3 className="text-white font-medium mb-1">{video.name}</h3>
                        <p className="text-gray-400 text-sm">{video.type}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Cast */}
            {cast.length > 0 && (
              <section>
                <h2 className="text-3xl font-bold text-white mb-8">{t('cast')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                  {cast.map((person) => (
                    <div key={person.id} className="text-center">
                      <div className="aspect-[3/4] bg-dark-700 rounded-lg overflow-hidden mb-3">
                        {person.profile_path ? (
                          <img
                            src={`${imageBaseUrl}${person.profile_path}`}
                            alt={person.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <h3 className="text-white font-medium text-sm mb-1">{person.name}</h3>
                      <p className="text-gray-400 text-xs">{person.character}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Comments */}
            {tvShow?.id && (
              <Suspense fallback={
                <section>
                  <div className="h-8 bg-dark-700 rounded-lg w-40 mb-6 animate-pulse" />
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="bg-dark-800/60 rounded-xl p-5 border border-dark-700/50 animate-pulse">
                        <div className="flex gap-4">
                          <div className="w-10 h-10 rounded-full bg-dark-700 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="h-4 bg-dark-700 rounded w-32 mb-2" />
                            <div className="h-3 bg-dark-700 rounded w-full mb-1" />
                            <div className="h-3 bg-dark-700 rounded w-2/3" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              }>
                <MovieComments movieId={tvShow.id} mediaType="tv" />
              </Suspense>
            )}

            {/* Similar TV Shows */}
            {similarShows.length > 0 && (
              <section>
                <h2 className="text-3xl font-bold text-white mb-8">{t('similarShows')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-6">
                  {similarShows.map((similarShow) => (
                    <button
                      key={similarShow.id}
                      onClick={() => {
                        navigate(`/tv/${similarShow.id}`);
                      }}
                      className="group bg-dark-800 rounded-xl overflow-hidden hover:scale-105 transition-transform duration-300"
                    >
                      <div className="aspect-[2/3] bg-dark-700">
                        {similarShow.poster_path ? (
                          <img
                            src={`${imageBaseUrl}${similarShow.poster_path}`}
                            alt={similarShow.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-medium text-sm line-clamp-2 group-hover:text-primary-400 transition-colors">
                          {similarShow.name}
                        </h3>
                        <p className="text-gray-400 text-xs mt-1">
                          {similarShow.first_air_date?.split('-')[0]}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
        onRegistrationSuccess={() => {
          setShowAuthModal(false);
        }}
      />
    </>
  );
}

export default TVDetail; 